import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, type Channel, type Webhook, type Delivery } from "../api";

export default function ChannelDetail() {
  const { id } = useParams<{ id: string }>();
  const [channel, setChannel] = useState<Channel | null>(null);
  const [hooks, setHooks] = useState<Webhook[] | null>(null);
  const [deliveries, setDeliveries] = useState<Delivery[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!id) return;
    try {
      const [ch, wh, dl] = await Promise.all([
        api.getChannel(id),
        api.listWebhooks(id),
        api.listDeliveries({ channelId: id, limit: 30 }),
      ]);
      setChannel(ch.channel);
      setHooks(wh.webhooks);
      setDeliveries(dl.deliveries);
    } catch (e: any) {
      setErr(e.message);
    }
  }

  useEffect(() => { load(); }, [id]);

  const ingestUrl = channel ? `${window.location.origin}/w/${channel.ingest_slug}` : "";

  async function addHook(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setErr(null);
    setBusy(true);
    try {
      await api.createWebhook(id, name.trim(), url.trim());
      setName(""); setUrl(""); setShowForm(false);
      await load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  async function toggleHook(h: Webhook) {
    try {
      await api.updateWebhook(h.id, { enabled: !h.enabled });
      await load();
    } catch (e: any) { setErr(e.message); }
  }

  async function removeHook(h: Webhook) {
    if (!confirm(`刪除 "${h.name}"？`)) return;
    try { await api.deleteWebhook(h.id); await load(); }
    catch (e: any) { setErr(e.message); }
  }

  async function copyIngest() {
    await navigator.clipboard.writeText(ingestUrl);
    alert("已複製");
  }

  if (!channel) {
    return <div className="container"><div className="empty"><span className="spinner" /> 載入中…</div></div>;
  }

  return (
    <div className="container">
      <Link to="/" className="back-link">← 返回列表</Link>
      <div className="header-row" style={{ marginTop: 10 }}>
        <h1>{channel.name}</h1>
      </div>

      {err && <div className="error">{err}</div>}

      <div className="card">
        <h2>LINE Webhook URL</h2>
        <div className="muted" style={{ marginBottom: 10 }}>把下面的網址貼到 LINE Developers → Messaging API → Webhook URL，並啟用「Use webhook」。</div>
        <div className="ingest-url">
          <span style={{ flex: 1 }}>{ingestUrl}</span>
          <button onClick={copyIngest}>複製</button>
        </div>
      </div>

      <div className="card">
        <div className="header-row">
          <h2 style={{ margin: 0 }}>下游 Webhook（{hooks?.length ?? 0}）</h2>
          {!showForm && <button className="primary" onClick={() => setShowForm(true)}>+ 新增</button>}
        </div>

        {showForm && (
          <form onSubmit={addHook} style={{ marginBottom: 16, background: "var(--panel-2)", padding: 16, borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
            <div className="field">
              <label>Webhook 名稱</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="例：n8n 主流程" required />
            </div>
            <div className="field">
              <label>目標 URL</label>
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." required />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="primary" type="submit" disabled={busy}>{busy ? "新增中…" : "新增"}</button>
              <button type="button" className="ghost" onClick={() => { setShowForm(false); setName(""); setUrl(""); }}>取消</button>
            </div>
          </form>
        )}

        {hooks && hooks.length === 0 ? (
          <div className="empty">尚未新增下游 webhook。加入後 LINE 來的訊息會同步轉發。</div>
        ) : (
          <div className="list">
            {hooks?.map((h) => (
              <div className="row" key={h.id}>
                <div className="meta">
                  <div className="name">
                    {h.name} <span className={`badge ${h.enabled ? "on" : "off"}`}>{h.enabled ? "啟用" : "停用"}</span>
                  </div>
                  <span className="sub">{h.target_url}</span>
                </div>
                <div className="actions">
                  <button onClick={() => toggleHook(h)}>{h.enabled ? "停用" : "啟用"}</button>
                  <button className="danger" onClick={() => removeHook(h)}>刪除</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <div className="header-row">
          <h2 style={{ margin: 0 }}>最近傳送紀錄</h2>
          <button className="ghost" onClick={load}>重新整理</button>
        </div>
        {deliveries && deliveries.length === 0 ? (
          <div className="empty">還沒有傳送紀錄。LINE 推送後會出現在這裡。</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>時間</th>
                <th>Webhook</th>
                <th>狀態</th>
                <th>HTTP</th>
                <th>重試</th>
                <th>耗時</th>
                <th>錯誤 / 回應</th>
              </tr>
            </thead>
            <tbody>
              {deliveries?.map((d) => {
                const hook = hooks?.find((h) => h.id === d.webhook_id);
                return (
                  <tr key={d.id}>
                    <td>{new Date(d.created_at).toLocaleString("zh-TW")}</td>
                    <td>{hook?.name ?? d.webhook_id.slice(0, 8)}</td>
                    <td><span className={`badge ${d.status === "success" ? "ok" : d.status === "failed" ? "warn" : "fail"}`}>{d.status}</span></td>
                    <td>{d.status_code ?? "—"}</td>
                    <td>{d.attempts}</td>
                    <td>{d.duration_ms ? `${d.duration_ms}ms` : "—"}</td>
                    <td style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {d.error_message ?? d.response_preview ?? ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
